import { useEffect, useRef, useState } from 'react';

interface CallStatusChipProps {
  /** Whether recording is paused. Only meaningful for practitioners. */
  isPaused: boolean;
  /** Render the recording dot + label. Clients typically false. */
  showRecording: boolean;
  /** Wall-clock instant the call actually started (`session.actualStart`, an
   *  ISO string), stamped server-side by the LiveKit `room_started` webhook on
   *  the first join and never overwritten on reconnect. Null until that webhook
   *  lands — i.e. only for the very first joiner, in the sliver before the
   *  stamp arrives. The timer anchors here so every participant reads the same
   *  elapsed and a leave/rejoin resumes from the true elapsed, not zero. */
  actualStart: string | null;
}

/**
 * Top-left status chip — the single source of truth for "this call is live
 * and (optionally) being recorded". Replaces the earlier floating
 * RecordingBadge, which felt orphaned. Structure:
 *
 *   ● REC  |  00:12:34
 *
 * The amber dot pulses while recording is active; turns grey while paused.
 * The duration counts up from `actualStart` (the shared, server-stamped call
 * start), so it is recording-independent, identical across participants, and
 * survives leave/rejoin. Before that stamp lands (first joiner only) it ticks
 * from the local connect moment, which reads 00:00 for the first person in.
 */
export function CallStatusChip({ isPaused, showRecording, actualStart }: CallStatusChipProps) {
  const duration = useCallDuration(actualStart);

  return (
    <output
      aria-live="polite"
      className="pointer-events-none absolute top-5 left-5 z-10 inline-flex items-center gap-3 rounded-full border border-white/8 bg-[#0B1614]/70 py-1.5 pr-4 pl-3 text-white/85 backdrop-blur-md"
    >
      {showRecording ? (
        <>
          <span className="relative flex h-2 w-2 items-center justify-center">
            <span
              aria-hidden="true"
              className={`absolute inline-flex h-full w-full rounded-full ${
                isPaused ? 'bg-transparent' : 'animate-ping bg-[#D4A017]/65'
              }`}
            />
            <span
              className={`relative inline-flex h-2 w-2 rounded-full ${
                isPaused ? 'bg-white/40' : 'bg-[#D4A017]'
              }`}
            />
          </span>
          <span className="tracking-eyebrow text-[10px] font-semibold text-white uppercase">
            {isPaused ? 'Paused' : 'Rec'}
          </span>
          <span aria-hidden="true" className="h-3 w-px bg-white/15" />
        </>
      ) : null}
      <span className="font-mono text-[12px] text-white/75 tabular-nums">
        {formatDuration(duration)}
      </span>
    </output>
  );
}

/**
 * Seconds elapsed since the call started. Anchors to `actualStart` when known
 * (authoritative + shared across participants); falls back to the local mount
 * instant while it is still null so the first joiner's timer ticks from 00:00
 * instead of freezing. Clamped at 0 so a clock skew or an early `actualStart`
 * can never show a negative duration.
 */
function useCallDuration(actualStart: string | null): number {
  // The local "connect moment" fallback, captured once on the first effect
  // run (Date.now() is impure, so it can't run during render). Stable
  // thereafter so it never drifts while we wait for `actualStart`.
  const localAnchorRef = useRef<number | null>(null);
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    localAnchorRef.current ??= Date.now();
    const parsed = actualStart ? Date.parse(actualStart) : Number.NaN;
    const anchorMs = Number.isNaN(parsed) ? localAnchorRef.current : parsed;
    const tick = () => {
      setSeconds(Math.max(0, Math.floor((Date.now() - anchorMs) / 1000)));
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => {
      window.clearInterval(id);
    };
  }, [actualStart]);

  return seconds;
}

function formatDuration(total: number): string {
  const safe = Math.max(0, Math.floor(total));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const secs = safe % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return hours > 0 ? `${pad(hours)}:${pad(minutes)}:${pad(secs)}` : `${pad(minutes)}:${pad(secs)}`;
}
